/**
 * # Fluent id helpers for institution settings
 *
 * @description
 * All id getters defined in this file match the ids in the output of
 * `to_fluent_messages` setting classmethods.
 *
 * @usage
 * For string type settings, use `getStringSettingFluentId()`.
 *
 * For JSON type settings, one class per setting has id getters.
 */

export const FLUENT_ID_PREFIX = "fi"; // used for all settings

/**
 * @param settingName SETTING_NAME field of setting as hyphenated lowercase
 */
export const getStringSettingFluentId = (settingName: string) => {
  return `${FLUENT_ID_PREFIX}-${settingName}`;
};

/**
 * @description Get translation ids by product id and field
 * @usage
 * ```
 * const CheckingFluentIds = new ProductFluentIds(1234);
 * CheckingFluentIds.getName(); // product name
 * CheckingFluentIds.getDetail(2); // detail by index
 * ```
 */
export class ProductFluentIds {
  private SETTING_NAME = "core-opening-products";

  public productId: string;

  constructor(productId: string) {
    this.productId = productId;
  }

  getName() {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-name`;
  }

  getDescription() {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-description`;
  }

  getMoreDetailsCopy() {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-more-details-copy`;
  }

  getDetail(detailIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-details${detailIndex}`;
  }

  getDisclosureCheckboxText(disclosureIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-disclosures${disclosureIndex}-checkbox-text`;
  }

  getDisclosureMarkdown(disclosureIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-disclosures${disclosureIndex}-markdown`;
  }

  getDisclosureLinkText(disclosureIndex: number, linkIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-disclosures${disclosureIndex}-links${linkIndex}-text`;
  }

  getMonthlyServiceFee() {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.productId}-monthly-service-fee`;
  }
}

/**
 * @usage
 * ```
 * const fluentIds = new DueDiligenceFluentIds("bao");
 * DueDiligenceFluentIds.getQuestion(0); // first due diligence question in BAO
 * ```
 */
export class DueDiligenceFluentIds {
  private SETTING_NAME: string;

  constructor(aoType: "cao" | "bao" | "consumer-slao" | "business-slao") {
    this.SETTING_NAME = `cdd-${aoType}-questions`;
  }

  getQuestion(questionIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${questionIndex}`;
  }

  getQuestionLabel(questionIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${questionIndex}-label`;
  }

  getDropdownQuestionOption(questionIndex: number, optionIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${questionIndex}-options${optionIndex}`;
  }

  getAnswerOptionDisplayText(questionIndex: number, optionIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${questionIndex}-answer-options${optionIndex}-display-text`;
  }

  getFollowUpQuestionPrefix(
    questionIndex: number,
    optionIndex: number,
    followUpIndex: number,
  ) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${questionIndex}-answer-options${optionIndex}-follow-up${followUpIndex}`;
  }
}

/**
 * @description Get translation ids for product-level account activity questions
 * @usage
 * ```
 * const ids = new AccountActivityQuestionIds("42");
 * ids.getQuestion(0); // first activity question on product 42
 * ```
 */
export class AccountActivityQuestionIds {
  private MODEL_NAME = "account-activity-question";

  public productId: string;

  constructor(productId: string) {
    this.productId = productId;
  }

  private prefix() {
    return `${FLUENT_ID_PREFIX}-${this.MODEL_NAME}-${this.productId}`;
  }

  getQuestion(questionIndex: number) {
    return `${this.prefix()}-${questionIndex}`;
  }

  getQuestionLabel(questionIndex: number) {
    return `${this.prefix()}-${questionIndex}-label`;
  }

  getDropdownQuestionOption(questionIndex: number, optionIndex: number) {
    return `${this.prefix()}-${questionIndex}-options${optionIndex}`;
  }

  getAnswerOptionDisplayText(questionIndex: number, optionIndex: number) {
    return `${this.prefix()}-${questionIndex}-answer-options${optionIndex}-display-text`;
  }

  getFollowUpQuestionPrefix(
    questionIndex: number,
    optionIndex: number,
    followUpIndex: number,
  ) {
    return `${this.prefix()}-${questionIndex}-answer-options${optionIndex}-follow-up${followUpIndex}`;
  }
}

export class SupportContactFluentIds {
  private SETTING_NAME = "support-contacts";

  getTitle(contactIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${contactIndex}-title`;
  }

  getNumberName(contactIndex: number, numberIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${contactIndex}-numbers${numberIndex}-name`;
  }
}

export class SupportFaqFluentIds {
  private SETTING_NAME = "support-faq";

  getCategory(categoryIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${categoryIndex}-category`;
  }

  getQuestion(categoryIndex: number, questionIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${categoryIndex}-questions${questionIndex}-question`;
  }

  getAnswer(categoryIndex: number, questionIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${categoryIndex}-questions${questionIndex}-answer`;
  }
}

export class SupportLinksFluentIds {
  private SETTING_NAME = "support-links";

  getText(linkIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}${linkIndex}-text`;
  }
}

export class OaoDisclosuresFluentIds {
  private SETTING_NAME = "oao-disclosures";

  getCheckboxText(disclosureIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-disclosures${disclosureIndex}-checkbox-text`;
  }

  getMarkdown(disclosureIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-disclosures${disclosureIndex}-markdown`;
  }

  getLinkText(disclosureIndex: number, linkIndex: number) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-disclosures${disclosureIndex}-links${linkIndex}-text`;
  }
}

type EligibilityModelType = "question" | "answer";
export class AccountEligibilityIds {
  private MODEL_NAME_PREFIX = "account-eligibility";

  private FIELD_NAME = "display-text";

  public modelType: EligibilityModelType;

  constructor(type: EligibilityModelType) {
    this.modelType = type;
  }

  getDisplayText(id: string) {
    return `${FLUENT_ID_PREFIX}-${this.MODEL_NAME_PREFIX}-${this.modelType}-${id}-${this.FIELD_NAME}`;
  }
}

export class PromoFluentIds {
  private SETTING_NAME = "institution-promo-data";

  private index: string;

  constructor(index: number) {
    this.index = index.toString();
  }

  getTitle(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.index}-title`;
  }

  getDescription(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.index}-description`;
  }

  getActionText(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.index}-action-text`;
  }
}

export class InstitutionFeatureFluentIds {
  private SETTING_NAME = "institution-features";

  getTopMenuItem(linkName: string) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-top-menu-item-${linkName
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .toLowerCase()
      .replace(/\s+/g, "-")}`;
  }

  getSidebarMenuItem(linkName: string) {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-sidebar-menu-item-${linkName
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .toLowerCase()
      .replace(/\s+/g, "-")}`;
  }
}

export class MarketingCampaignFluentIds {
  private SETTING_NAME = "marketing-campaign";

  private uuid: string;

  constructor(uuid: string) {
    this.uuid = uuid;
  }

  getTitle(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.uuid}-asset-config-title`;
  }

  getBody(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.uuid}-asset-config-body`;
  }

  getLinkText(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.uuid}-asset-config-link-text`;
  }
}

/**
 * @usage
 * ```
 * const fluentIds = new PromptGroupFluentIds("some-uuid");
 * fluentIds.getExternalTitle(); // prompt group external title
 * fluentIds.getDescription(); // prompt group description
 * ```
 */
export class PromptGroupFluentIds {
  private SETTING_NAME = "guide-prompt-group";

  private id: string;

  constructor(id: string) {
    this.id = id;
  }

  getExternalTitle(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.id}-external-title`;
  }

  getDescription(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.id}-description`;
  }
}

/**
 * @usage
 * ```
 * const fluentIds = new PromptConfigurationFluentIds("some-uuid");
 * fluentIds.getTitle(); // prompt configuration title
 * fluentIds.getDescription(); // prompt configuration description
 * ```
 */
export class PromptConfigurationFluentIds {
  private SETTING_NAME = "guide-prompt-configuration";

  private id: string;

  constructor(id: string) {
    this.id = id;
  }

  getTitle(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.id}-title`;
  }

  getDescription(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.id}-description`;
  }

  getLinkText(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.id}-link-text`;
  }
}

/**
 * @usage
 * ```
 * const fluentIds = new OAuthApplicationFluentIds("9bde7151-badf-4cab-902e-c7ece17b4132");
 * fluentIds.getName(); // OAuth application name
 * fluentIds.getTagline(); // OAuth application tagline
 * fluentIds.getDescription(); // OAuth application description
 * ```
 */
export class OAuthApplicationFluentIds {
  private SETTING_NAME = "oauth-app";

  private uuid: string;

  constructor(uuid: string) {
    this.uuid = uuid;
  }

  getName(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.uuid}-name`;
  }

  getTagline(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.uuid}-tagline`;
  }

  getDescription(): string {
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${this.uuid}-description`;
  }
}

/**
 * Ids are keyed by the purpose's slug, not its index, so reordering the
 * setting keeps translations attached. Mirrors `LendingHomeEquityLoanPurposes.slug`.
 */
export class HomeEquityLoanPurposeFluentIds {
  private SETTING_NAME = "lending-home-equity-loan-purposes";

  getLabel(label: string): string {
    const slug = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return `${FLUENT_ID_PREFIX}-${this.SETTING_NAME}-${slug}`;
  }
}
